import React, { useEffect, useState } from 'react';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Button from '../../components/button/Button';
import Icon from '../../components/icon/Icon';
import Image from '../../components/image/Image';
import Table from '../../components/table/table';
import TableSkeleton from '../../components/TableSkeleton/TableSkeleton';
import NewCommissionModal from './NewCommissionModal';
import { getCommissions, createCommission } from '../../services/GuaranteesService';
import { getCommissionColumns, CommissionData } from '../../utils/commissionColumns';
import { ITableData } from '../../types/interfaces';

interface CommissionsTabProps {
  guaranteeId: string;
  guaranteeType: 'loan' | 'check' | 'promissory_note' | 'commercial_endorsement';
  idGlobal: string;
}

const CommissionsTab: React.FC<CommissionsTabProps> = ({ guaranteeId, guaranteeType, idGlobal }) => {
  const [commissions, setCommissions] = useState<CommissionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tableData, setTableData] = useState<ITableData>({
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0,
  });

  useEffect(() => {
    fetchCommissions();
  }, [guaranteeId]);

  const fetchCommissions = async () => {
    setLoading(true);
    setError(null);

    try {
      const typeMap = {
        loan: 'loans' as const,
        check: 'checks' as const,
        promissory_note: 'promissory-notes' as const,
        commercial_endorsement: 'commercial-endorsements' as const,
      };

      const response = await getCommissions(typeMap[guaranteeType], idGlobal);
      
      if (response.success && 'data' in response) {
        const commissionsData = Array.isArray(response.data) 
          ? response.data 
          : response.data.content || [];
        
        // Map line type based on guarantee type
        const getLineType = () => {
          if (guaranteeType === 'loan') return 'Préstamos';
          if (guaranteeType === 'check') return 'Cheque';
          if (guaranteeType === 'promissory_note') return 'Pagaré';
          return 'Aval Comercial';
        };

        const enrichedCommissions = commissionsData.map((commission: any) => ({
          id: commission.id,
          name: commission.name,
          line_type: getLineType(),
          commission_type_name: commission.commission_type_name || commission.commission_type?.name || '-',
          amount: commission.amount,
        }));

        setCommissions(enrichedCommissions);
        
        // Handle pagination if present
        if ('data' in response && response.data.total_elements !== undefined) {
          setTableData({
            page: response.data.page || 0,
            size: response.data.size || 10,
            totalElements: response.data.total_elements,
            totalPages: response.data.total_pages || 1,
          });
        } else {
          setTableData({
            page: 0,
            size: commissionsData.length,
            totalElements: commissionsData.length,
            totalPages: 1,
          });
        }
      } else {
        setCommissions([]);
      }
    } catch (err) {
      console.error('Error fetching commissions:', err);
      setError('Error al cargar las comisiones');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSaveCommission = async (commissionData: {
    commission_type_id: number;
    name: string;
    amount: number;
  }) => {
    const typeMap = {
      loan: 'loans' as const,
      check: 'checks' as const,
      promissory_note: 'promissory-notes' as const,
      commercial_endorsement: 'commercial-endorsements' as const,
    };

    await createCommission(typeMap[guaranteeType], idGlobal, commissionData);
    await fetchCommissions();
  };

  const columns = getCommissionColumns();

  if (loading) {
    return (
      <Card margin={{ top: '4' }}>
        <TableSkeleton rows={10} columns={columns.length} />
      </Card>
    );
  }

  if (error) {
    return (
      <Card
        display='grid'
        justifyContent='center'
        margin={{ top: '11', bottom: '11' }}
        width='100%'
      >
        <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
          <Image name='folder_empty' />
        </Card>
        <P variant='primary' align='center' withMargin={false} weight='bold'>
          {error}
        </P>
      </Card>
    );
  }

  return (
    <>
      <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ top: '4' }}>
        <H
          type='5'
          variant='neutral'
          weight='bold'
          modifier='strong'
          withMargin={false}
        >
          Comisiones por líneas
        </H>
        <Button variant='tertiary' onClick={handleOpenModal}>
          <Card display='flex' alignItems='center' gap='8px'>
            <Icon name='plus_icon' variant='info' width={20} height={20} />
            Nueva comisión
          </Card>
        </Button>
      </Card>

      {commissions.length === 0 ? (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='100%'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No hay comisiones registradas.
          </P>
          <P variant='primary' align='center'>
            Podés crear una nueva comisión.
          </P>
        </Card>
      ) : (
        <Card margin={{ top: '4' }} justifyContent='center' alignItems='center'>
          <Table
            columns={columns}
            data={commissions}
            tableData={tableData}
            handleChangePage={() => {}}
          />
        </Card>
      )}

      <NewCommissionModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveCommission}
      />
    </>
  );
};

export default CommissionsTab;
